import { useEffect, useState } from "react"
import { Localized, useLocalization } from "@fluent/react"
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItemButton, ListItemText, Paper, Stack, TextField, Typography } from "@mui/material"
import { APIError, unknownError } from "../../../app/appTypes"
import { CancelButton } from "../../../app/components/CancelButton"
import { LpField } from "../../../app/components/LpField"
import { SaveButton } from "../../../app/components/SaveButton"
import { useAppDispatch } from "../../../app/hooks"
import { LoadingContainer } from "../../../app/LoadingContainer"
import { RichTextEditor } from "../../../app/RichTextEditor"
import {
    loadOperationalDashboardAreaConfiguration,
    OperationalDashboardAreaContentConfigurationDto,
    OperationalDashboardContentSourceTypeEnum,
    SaveAreaConfigurationRequest,
    SaveAreaTopicRequest,
    isMeetingMinuteContent,
    toSaveAreaConfigurationRequest,
    updateOperationalDashboardAreaConfiguration,
} from "../operationalDashboards"

type OperationalDashboardAreaDialogProps = {
    dashboardId: number
    areaId: number
    onClose: () => void
    onSaved: () => void
}

export const OperationalDashboardAreaDialog = ({ dashboardId, areaId, onClose, onSaved }: OperationalDashboardAreaDialogProps) => {
    const dispatch = useAppDispatch()
    const { l10n } = useLocalization()
    const [form, setForm] = useState<SaveAreaConfigurationRequest | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const [serverError, setServerError] = useState<APIError | null>(null)
    const [hasStaticContent, setHasStaticContent] = useState(false)
    const [hasUnsupportedContent, setHasUnsupportedContent] = useState(false)
    const [hiddenTopicIds, setHiddenTopicIds] = useState<Set<number>>(new Set())
    const [retry, setRetry] = useState(0)
    const [selectedTopicIndex, setSelectedTopicIndex] = useState<number | null>(null)
    const [editorKey, setEditorKey] = useState(0)

    const isStaticContent = (content: OperationalDashboardAreaContentConfigurationDto) =>
        content.sourceType === OperationalDashboardContentSourceTypeEnum.STATIC_TEXT || content.sourceType === 1

    useEffect(() => {
        let active = true
        setIsLoading(true)
        setServerError(null)
        setForm(null)
        setSelectedTopicIndex(null)
        dispatch(loadOperationalDashboardAreaConfiguration({ dashboardId, areaId })).then(result => {
            if (!active) return
            if (loadOperationalDashboardAreaConfiguration.fulfilled.match(result)) {
                const configuration = result.payload
                setHasStaticContent(configuration.contents.some(isStaticContent))
                setHasUnsupportedContent(configuration.contents.some(content => !isMeetingMinuteContent(content) && !isStaticContent(content)))
                setHiddenTopicIds(new Set(configuration.contents.filter(content => isMeetingMinuteContent(content) && !content.isVisible)
                    .map(content => content.id)))
                setForm(toSaveAreaConfigurationRequest(configuration))
                setSelectedTopicIndex(configuration.contents.some(isMeetingMinuteContent) ? 0 : null)
                setEditorKey(previous => previous + 1)
            } else {
                setServerError(result.payload ?? unknownError())
            }
            setIsLoading(false)
        })
        return () => { active = false }
    }, [dispatch, dashboardId, areaId, retry])

    const updateForm = (changes: Partial<SaveAreaConfigurationRequest>) => {
        setForm(previous => previous ? { ...previous, ...changes } : previous)
    }

    const updateTopic = (index: number, changes: Partial<SaveAreaTopicRequest>) => {
        setForm(previous => previous ? {
            ...previous,
            topics: previous.topics.map((topic, position) => position === index ? { ...topic, ...changes } : topic),
        } : previous)
    }

    const addTopic = () => {
        if (!form || isSaving) return
        updateForm({
            topics: [...form.topics, {
                id: null,
                name: "",
                description: "",
                displayOrder: Math.max(-1, ...form.topics.map(topic => topic.displayOrder)) + 1,
            }],
        })
        setSelectedTopicIndex(form.topics.length)
        setEditorKey(previous => previous + 1)
    }

    const removeTopic = (index: number) => {
        if (!form || isSaving || !form.topics[index]) return
        updateForm({ topics: form.topics.filter((_, position) => position !== index) })
        setSelectedTopicIndex(previous => previous === null ? null
            : previous === index ? (form.topics.length > 1 ? Math.min(index, form.topics.length - 2) : null)
                : previous > index ? previous - 1 : previous)
        setEditorKey(previous => previous + 1)
    }

    const selectedTopic = selectedTopicIndex === null ? null : form?.topics[selectedTopicIndex] ?? null

    const canSave = !hasUnsupportedContent && !!form?.name.trim() && form.topics.every(topic =>
        !!topic.name.trim() && topic.name.length <= 1000 && Number.isInteger(topic.displayOrder)
    )

    const handleSave = async () => {
        if (!form || !canSave || isSaving) return
        setIsSaving(true)
        setServerError(null)
        const result = await dispatch(updateOperationalDashboardAreaConfiguration({
            id: dashboardId,
            areaId,
            dto: {
                ...form,
                name: form.name.trim(),
                topics: form.topics.map(topic => ({ ...topic, name: topic.name.trim() })),
            },
        }))
        if (updateOperationalDashboardAreaConfiguration.fulfilled.match(result) && result.payload === true) {
            setIsSaving(false)
            onSaved()
        } else {
            setServerError(updateOperationalDashboardAreaConfiguration.rejected.match(result)
                ? result.payload ?? unknownError() : unknownError())
            setIsSaving(false)
        }
    }

    return (
        <Dialog open onClose={isSaving ? undefined : onClose} fullWidth maxWidth="lg" aria-labelledby="operational-dashboard-area-dialog-title"
            PaperProps={{ sx: { minHeight: "min(780px, calc(100vh - 64px))" } }}>
            <DialogTitle id="operational-dashboard-area-dialog-title" sx={{ bgcolor: "grey.200", color: "text.primary", fontWeight: "bold" }}>
                <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
                    <Localized id="operational-dashboard-tile-settings">_Ustawienia kafelka</Localized>
                    {form?.companyDepartmentId != null && (
                        <Chip size="small" color="primary" variant="outlined"
                            label={l10n.getString("operational-dashboard-department-linked-tile", null, "_Kafelek powiązany z działem")} />
                    )}
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                {serverError && (
                    <Alert sx={{ mb: 2 }} onClose={() => setServerError(null)} severity="error">
                        {serverError.kind === "http" && serverError.problem ? (
                            <Localized id={serverError.problem.title}>
                                <span>{serverError.problem.detail || serverError.problem.title}</span>
                            </Localized>
                        ) : serverError.kind === "connection" ? (
                            <Localized id="connection-issue">_Problem z połączeniem</Localized>
                        ) : serverError.kind === "http" ? (
                            `${l10n.getString("error", null, "_Błąd")} (HTTP ${serverError.status})`
                        ) : (
                            <Localized id="error-unknown">_Wystąpił nieoczekiwany problem</Localized>
                        )}
                    </Alert>
                )}
                {isLoading ? <LoadingContainer /> : form ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
                        <TextField label={l10n.getString("name", null, "_Nazwa")} required fullWidth
                            value={form.name} onChange={event => updateForm({ name: event.target.value })} disabled={isSaving} />
                        <TextField label={l10n.getString("description", null, "_Opis")} fullWidth multiline
                            value={form.description} onChange={event => updateForm({ description: event.target.value })} disabled={isSaving} />
                        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                            <TextField label={l10n.getString("operational-dashboard-responsible-user", null, "_Login odpowiedzialnego")}
                                fullWidth value={form.responsibleUserName ?? ""}
                                onChange={event => updateForm({ responsibleUserName: event.target.value || null })} disabled={isSaving} />
                            <TextField label={l10n.getString("responsible-person", null, "_Osoba odpowiedzialna")}
                                fullWidth value={form.responsibleName ?? ""}
                                onChange={event => updateForm({ responsibleName: event.target.value || null })} disabled={isSaving} />
                            <TextField label={l10n.getString("operational-dashboard-responsible-email", null, "_Email odpowiedzialnego")}
                                fullWidth type="email" value={form.responsibleEmail ?? ""}
                                onChange={event => updateForm({ responsibleEmail: event.target.value || null })} disabled={isSaving} />
                        </Stack>
                        {hasStaticContent && (
                            <Alert severity="info">
                                <Localized id="operational-dashboard-static-content-unchanged">
                                    _Treści statyczne nie są zmieniane w tym formularzu.
                                </Localized>
                            </Alert>
                        )}
                        {hasUnsupportedContent && (
                            <Alert severity="error">
                                <Localized id="operational-dashboard-unsupported-content">
                                    _Nieznany typ zawartości kafelka. Zapis jest zablokowany, aby nie utracić tematów.
                                </Localized>
                            </Alert>
                        )}
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(220px, 1fr) minmax(0, 2fr)" }, gap: 2 }}>
                            <Paper variant="outlined" sx={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ p: 2 }}>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        <Localized id="operational-dashboard-current-topics">_Bieżące tematy i zadania</Localized>
                                    </Typography>
                                    <Button variant="outlined" size="small" onClick={addTopic} disabled={isSaving} sx={{ flexShrink: 0 }}>
                                        <Localized id="operational-dashboard-add-topic">_Dodaj temat</Localized>
                                    </Button>
                                </Stack>
                                <List disablePadding sx={{ maxHeight: 440, overflowY: "auto" }}>
                                    {form.topics.map((topic, index) => (
                                        <ListItemButton key={topic.id ?? `new-${index}`} selected={selectedTopicIndex === index} disabled={isSaving}
                                            onClick={() => {
                                                if (selectedTopicIndex === index) return
                                                setSelectedTopicIndex(index)
                                                setEditorKey(previous => previous + 1)
                                            }}>
                                            <Box sx={{ mr: 1.5, flexShrink: 0 }}><LpField value={index + 1} /></Box>
                                            <ListItemText
                                                primary={topic.name.trim() || l10n.getString("operational-dashboard-new-topic", null, "_Nowy temat")}
                                                secondary={topic.id !== null && hiddenTopicIds.has(topic.id)
                                                    ? l10n.getString("operational-dashboard-hidden-topic", null, "_Temat ukryty") : undefined}
                                                primaryTypographyProps={{ noWrap: true }}
                                            />
                                        </ListItemButton>
                                    ))}
                                </List>
                            </Paper>
                            <Paper variant="outlined" sx={{ p: 2, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                                {selectedTopic && selectedTopicIndex !== null ? (
                                    <>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                                            <Typography variant="subtitle1" fontWeight={600}>
                                                <Localized id="operational-dashboard-topic-details">_Edycja tematu</Localized>
                                            </Typography>
                                            <Button size="small" color="error" disabled={isSaving} onClick={() => removeTopic(selectedTopicIndex)}>
                                                <Localized id="operational-dashboard-remove-topic">_Usuń temat</Localized>
                                            </Button>
                                        </Stack>
                                        <TextField label={l10n.getString("operational-dashboard-topic-name", null, "_Nazwa tematu")}
                                            required fullWidth value={selectedTopic.name} inputProps={{ maxLength: 1000 }}
                                            onChange={event => updateTopic(selectedTopicIndex, { name: event.target.value })} disabled={isSaving} />
                                        <RichTextEditor key={editorKey} label={l10n.getString("description", null, "_Opis")}
                                            value={selectedTopic.description ?? ""} editable={!isSaving}
                                            onValueChange={description => updateTopic(selectedTopicIndex, { description })} />
                                    </>
                                ) : (
                                    <Typography color="text.secondary">
                                        <Localized id="operational-dashboard-select-topic">_Wybierz temat lub dodaj nowy.</Localized>
                                    </Typography>
                                )}
                            </Paper>
                        </Box>
                    </Box>
                ) : serverError && (
                    <Button onClick={() => setRetry(value => value + 1)}>
                        <Localized id="retry">_Ponów</Localized>
                    </Button>
                )}
            </DialogContent>
            <DialogActions>
                <SaveButton loading={isSaving} disabled={!canSave || isLoading} onClick={handleSave} />
                <CancelButton loading={isSaving} disabled={isSaving} onClick={onClose} />
            </DialogActions>
        </Dialog>
    )
}
